import { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonTextarea,
  IonText,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { SEND_FEEDBACK } from '../../graphql/mutations/sendFeedback';
import type {
  SendFeedbackMutation,
  SendFeedbackMutationVariables,
  FeedbackEntityTypeEnum,
} from '../../types/graphql-generated';
import { useUserAccount } from '../../hooks/useUserAccount';

const MAX_LENGTH = 512;

interface SendFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: FeedbackEntityTypeEnum;
  entityId: string;
}

export const SendFeedbackModal = ({
  isOpen,
  onClose,
  entityType,
  entityId,
}: SendFeedbackModalProps) => {
  const [text, setText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [presentToast] = useIonToast();
  const { userAccount } = useUserAccount();

  const [sendFeedback, { loading }] = useMutation<
    SendFeedbackMutation,
    SendFeedbackMutationVariables
  >(SEND_FEEDBACK);

  const intro =
    entityType === 'MATCH'
      ? '¿Has detectado algún error o tienes algún comentario sobre este partido? Cuéntanoslo y lo revisaremos.'
      : '¿Has detectado algún error o tienes algún comentario sobre este jugador? Cuéntanoslo y lo revisaremos.';

  const handleClose = () => {
    setText('');
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const trimmed = text.trim();
    if (!trimmed || !userAccount) return;

    try {
      const { data } = await sendFeedback({
        variables: {
          entityType,
          entityId,
          userId: userAccount.id,
          text: trimmed,
        },
      });

      const result = data?.sendFeedback;
      if (result?.success) {
        setText('');
        presentToast({
          message: '¡Gracias! Hemos recibido tu comentario.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        onClose();
      } else {
        setErrorMessage(result?.errors?.[0] ?? 'No se ha podido enviar tu comentario.');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      setErrorMessage('No se ha podido enviar tu comentario. Inténtalo de nuevo más tarde.');
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Enviar comentario</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonText>
          <p className="text-sm text-gray-600 mb-4">{intro}</p>
        </IonText>

        <IonTextarea
          label="Tu comentario"
          labelPlacement="floating"
          fill="outline"
          autoGrow
          rows={5}
          maxlength={MAX_LENGTH}
          counter
          value={text}
          onIonInput={(e) => setText(e.detail.value || '')}
          placeholder="Escribe aquí tu comentario"
        />

        {errorMessage && (
          <IonText color="danger">
            <p className="text-sm mt-3">{errorMessage}</p>
          </IonText>
        )}

        <div className="mt-6">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={loading || !text.trim()}
          >
            {loading ? <IonSpinner name="crescent" /> : 'Enviar'}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
